export default class BeforeAfter {
  constructor(element) {
    this.element = element;
    this.before = null;
    this.after = null;
    this.handle = null;

    this.options = {
      labelBefore: element.dataset.labelBefore || 'Avant',
      labelAfter: element.dataset.labelAfter || 'Après',
      keyboardStep: 5, // % par appui de flèche
      hintThreshold: 0.6, // % de visibilité avant de lancer l'invitation
    };

    this.percent = 50;
    this.isDragging = false;
    this.hasInteracted = false;
    this.animationId = 0;

    this.init();
  }

  init() {
    this.before = this.element.querySelector('.before');
    this.after = this.element.querySelector('.after');
    this.handle = this.element.querySelector('.comparison-handle');

    this.createLabels();
    this.setupAccessibility();

    // Pointer Events = souris + doigt + stylet avec un seul jeu d'écouteurs
    this.element.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.element.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.element.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.element.addEventListener('pointercancel', this.onPointerUp.bind(this));
    this.handle.addEventListener('keydown', this.onKeyDown.bind(this));

    this.observeHint();
  }

  /* ---------- Étiquettes ---------- */

  createLabels() {
    this.before.append(
      this.buildLabel(this.options.labelBefore, 'comparison-label--before'),
    );
    this.after.append(
      this.buildLabel(this.options.labelAfter, 'comparison-label--after'),
    );
  }

  buildLabel(text, modifier) {
    const label = document.createElement('span');
    label.className = `comparison-label ${modifier}`;
    label.textContent = text;
    label.setAttribute('aria-hidden', 'true');
    return label;
  }

  /* ---------- Accessibilité (clavier) ---------- */

  setupAccessibility() {
    this.handle.setAttribute('role', 'slider');
    this.handle.setAttribute('tabindex', '0');
    this.handle.setAttribute(
      'aria-label',
      `Comparaison ${this.options.labelBefore} / ${this.options.labelAfter}`,
    );
    this.handle.setAttribute('aria-valuemin', '0');
    this.handle.setAttribute('aria-valuemax', '100');
    this.handle.setAttribute('aria-valuenow', '50');
  }

  onKeyDown(event) {
    let next = null;
    if (event.key === 'ArrowLeft')
      next = this.percent - this.options.keyboardStep;
    if (event.key === 'ArrowRight')
      next = this.percent + this.options.keyboardStep;
    if (next === null) return;

    event.preventDefault();
    this.stopHint();
    this.updateSlider(Math.max(0, Math.min(100, next)));
  }

  /* ---------- Glissement ---------- */

  // Calcule la position en pourcentage
  getPercent(clientX) {
    const rect = this.element.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(100, (x / rect.width) * 100));
  }

  // Met à jour le slider
  updateSlider(percent) {
    this.percent = percent;
    this.before.style.clipPath = `inset(0 ${100 - percent}% 0 0)`;
    this.handle.style.left = percent + '%';
    this.handle.setAttribute('aria-valuenow', Math.round(percent));
  }

  onPointerDown(event) {
    this.stopHint();
    this.isDragging = true;
    this.element.setPointerCapture(event.pointerId);

    // À la souris, un clic place directement la poignée.
    // Au doigt, on attend le mouvement : sinon un simple défilement
    // vertical de la page ferait "sauter" le slider.
    if (event.pointerType === 'mouse') {
      this.updateSlider(this.getPercent(event.clientX));
    }
  }

  onPointerMove(event) {
    if (!this.isDragging) return;
    this.updateSlider(this.getPercent(event.clientX));
  }

  onPointerUp() {
    this.isDragging = false;
  }

  /* ---------- Invitation à glisser ---------- */

  // Lance l'animation une seule fois, quand le slider est bien visible
  observeHint() {
    this.element.classList.add('is-hinting'); // pulsation de la poignée

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.playHint();
      },
      { threshold: this.options.hintThreshold },
    );
    observer.observe(this.element);
  }

  async playHint() {
    if (this.hasInteracted) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = ++this.animationId;
    const steps = [
      [30, 600],
      [70, 900],
      [50, 600],
    ];

    for (const [target, duration] of steps) {
      await this.animateTo(target, duration, id);
      if (id !== this.animationId) return; // annulé par l'utilisateur
    }
  }

  animateTo(target, duration, id) {
    return new Promise((resolve) => {
      const start = this.percent;
      const startTime = performance.now();

      const frame = (now) => {
        if (id !== this.animationId) return resolve();

        const t = Math.min((now - startTime) / duration, 1);
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // easeInOut
        this.updateSlider(start + (target - start) * eased);

        if (t < 1) requestAnimationFrame(frame);
        else resolve();
      };
      requestAnimationFrame(frame);
    });
  }

  // Annule l'animation et la pulsation dès la première interaction
  stopHint() {
    this.hasInteracted = true;
    this.animationId++;
    this.element.classList.remove('is-hinting');
  }
}
